import * as THREE from 'three';

export interface BurstOptions {
  count: number;
  colors: readonly THREE.ColorRepresentation[];
  /** Initial speed range. */
  speed?: [number, number];
  /** Extra upward velocity. */
  up?: number;
  /** Horizontal spawn radius. */
  spread?: number;
  gravity?: number;
  drag?: number;
  size?: [number, number];
  life?: [number, number];
  /** 0 = soft round, 1 = sparkle star, 2 = petal. */
  shape?: 0 | 1 | 2;
}

const MAX_PARTICLES = 1200;

const VERTEX = /* glsl */ `
  attribute float aSize;
  attribute float aAlpha;
  attribute float aShape;
  attribute float aSpin;
  attribute vec3 color;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vShape;
  varying float vSpin;
  uniform float uScale;
  void main() {
    vColor = color;
    vAlpha = aAlpha;
    vShape = aShape;
    vSpin = aSpin;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * uScale / -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAGMENT = /* glsl */ `
  varying vec3 vColor;
  varying float vAlpha;
  varying float vShape;
  varying float vSpin;
  void main() {
    vec2 p = gl_PointCoord - 0.5;
    float c = cos(vSpin), s = sin(vSpin);
    p = mat2(c, -s, s, c) * p;
    float alpha;
    if (vShape < 0.5) {
      alpha = smoothstep(0.5, 0.15, length(p));
    } else if (vShape < 1.5) {
      // Four-pointed sparkle.
      float d = min(abs(p.x) * 8.0 + abs(p.y), abs(p.y) * 8.0 + abs(p.x));
      alpha = smoothstep(0.5, 0.1, d) + smoothstep(0.18, 0.0, length(p));
    } else {
      // Petal: a soft ellipse.
      vec2 q = p * vec2(1.0, 2.0);
      alpha = smoothstep(0.5, 0.35, length(q));
    }
    alpha *= vAlpha;
    if (alpha < 0.02) discard;
    gl_FragColor = vec4(vColor, alpha);
    #include <colorspace_fragment>
  }
`;

const _color = new THREE.Color();
const _dir = new THREE.Vector3();

/** A single pooled GPU point cloud for every short-lived particle effect. */
export class Particles {
  readonly points: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private readonly positions = new Float32Array(MAX_PARTICLES * 3);
  private readonly colors = new Float32Array(MAX_PARTICLES * 3);
  private readonly sizes = new Float32Array(MAX_PARTICLES);
  private readonly alphas = new Float32Array(MAX_PARTICLES);
  private readonly shapes = new Float32Array(MAX_PARTICLES);
  private readonly spins = new Float32Array(MAX_PARTICLES);
  private readonly velocities = new Float32Array(MAX_PARTICLES * 3);
  private readonly life = new Float32Array(MAX_PARTICLES);
  private readonly maxLife = new Float32Array(MAX_PARTICLES);
  private readonly gravity = new Float32Array(MAX_PARTICLES);
  private readonly drag = new Float32Array(MAX_PARTICLES);
  private readonly baseSize = new Float32Array(MAX_PARTICLES);
  private cursor = 0;

  constructor() {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'aSize',
      new THREE.BufferAttribute(this.sizes, 1).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'aAlpha',
      new THREE.BufferAttribute(this.alphas, 1).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'aShape',
      new THREE.BufferAttribute(this.shapes, 1).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'aSpin',
      new THREE.BufferAttribute(this.spins, 1).setUsage(THREE.DynamicDrawUsage),
    );
    this.points = new THREE.Points(
      geometry,
      new THREE.ShaderMaterial({
        vertexShader: VERTEX,
        fragmentShader: FRAGMENT,
        uniforms: { uScale: { value: 400 } },
        transparent: true,
        depthWrite: false,
      }),
    );
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
  }

  /** Keeps particle sizes in world units for the current viewport and field of view. */
  setViewport(heightPx: number, fovDegrees: number): void {
    const halfFov = THREE.MathUtils.degToRad(fovDegrees) / 2;
    this.points.material.uniforms.uScale!.value = heightPx / (2 * Math.tan(halfFov));
  }

  burst(origin: THREE.Vector3, o: BurstOptions): void {
    const [smin, smax] = o.speed ?? [1, 3];
    const [szMin, szMax] = o.size ?? [0.15, 0.3];
    const [lMin, lMax] = o.life ?? [0.6, 1.2];
    for (let n = 0; n < o.count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      const angle = Math.random() * Math.PI * 2;
      const r = Math.random() * (o.spread ?? 0.3);
      this.positions[i * 3] = origin.x + Math.cos(angle) * r;
      this.positions[i * 3 + 1] = origin.y;
      this.positions[i * 3 + 2] = origin.z + Math.sin(angle) * r;
      const speed = smin + Math.random() * (smax - smin);
      const dir = _dir
        .set(Math.random() - 0.5, Math.random() * 0.6 + 0.2, Math.random() - 0.5)
        .normalize();
      this.velocities[i * 3] = dir.x * speed;
      this.velocities[i * 3 + 1] = dir.y * speed + (o.up ?? 0);
      this.velocities[i * 3 + 2] = dir.z * speed;
      _color.set(o.colors[Math.floor(Math.random() * o.colors.length)] ?? '#ffffff');
      this.colors[i * 3] = _color.r;
      this.colors[i * 3 + 1] = _color.g;
      this.colors[i * 3 + 2] = _color.b;
      this.baseSize[i] = szMin + Math.random() * (szMax - szMin);
      this.maxLife[i] = this.life[i] = lMin + Math.random() * (lMax - lMin);
      this.gravity[i] = o.gravity ?? 6;
      this.drag[i] = o.drag ?? 1.5;
      this.shapes[i] = o.shape ?? 0;
      this.spins[i] = Math.random() * Math.PI * 2;
    }
  }

  // Presets -----------------------------------------------------------------

  petals(origin: THREE.Vector3, colors: readonly string[]): void {
    this.burst(origin, {
      count: 26,
      colors,
      speed: [1.5, 3.5],
      up: 2.2,
      spread: 0.6,
      gravity: 4,
      drag: 1.8,
      size: [0.18, 0.32],
      life: [0.9, 1.6],
      shape: 2,
    });
  }

  sparkles(
    origin: THREE.Vector3,
    count = 18,
    colors: readonly string[] = ['#fff6b0', '#ffffff', '#ffd23f'],
  ): void {
    this.burst(origin, {
      count,
      colors,
      speed: [0.8, 2.4],
      up: 1.5,
      spread: 0.6,
      gravity: 0.5,
      drag: 2,
      size: [0.18, 0.36],
      life: [0.7, 1.3],
      shape: 1,
    });
  }

  water(origin: THREE.Vector3): void {
    this.burst(origin, {
      count: 16,
      colors: ['#8fd3ff', '#bfe6ff', '#5fb4ff'],
      speed: [1, 2.5],
      up: 2.5,
      spread: 0.7,
      gravity: 12,
      drag: 0.6,
      size: [0.1, 0.18],
      life: [0.4, 0.8],
    });
  }

  dust(origin: THREE.Vector3, count = 14): void {
    this.burst(origin, {
      count,
      colors: ['#c9a27a', '#e0c3a0', '#a98466'],
      speed: [0.6, 1.8],
      up: 0.8,
      spread: 0.8,
      gravity: 1.5,
      drag: 2.5,
      size: [0.2, 0.4],
      life: [0.5, 1.0],
    });
  }

  coins(origin: THREE.Vector3, count = 12): void {
    this.burst(origin, {
      count,
      colors: ['#ffd23f', '#ffe680', '#ffb400'],
      speed: [1.5, 3],
      up: 3.5,
      spread: 0.3,
      gravity: 9,
      drag: 0.8,
      size: [0.2, 0.3],
      life: [0.7, 1.1],
      shape: 1,
    });
  }

  smoke(origin: THREE.Vector3): void {
    this.burst(origin, {
      count: 1,
      colors: ['#ffffff', '#f2f2f2'],
      speed: [0.2, 0.4],
      up: 0.9,
      spread: 0.15,
      gravity: -0.15,
      drag: 0.3,
      size: [0.9, 1.4],
      life: [2.5, 3.5],
    });
  }

  confetti(origin: THREE.Vector3): void {
    this.burst(origin, {
      count: 60,
      colors: ['#ff8fab', '#ffd23f', '#7cc6fe', '#9be38a', '#b79cff'],
      speed: [3, 6],
      up: 4,
      spread: 0.4,
      gravity: 6,
      drag: 1.2,
      size: [0.18, 0.3],
      life: [1.2, 2],
      shape: 2,
    });
  }

  update(dt: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.life[i]! <= 0) {
        this.alphas[i] = 0;
        continue;
      }
      const life = (this.life[i] = this.life[i]! - dt);
      const t = Math.max(0, life / this.maxLife[i]!);
      const drag = Math.exp(-this.drag[i]! * dt);
      this.velocities[i * 3] = this.velocities[i * 3]! * drag;
      this.velocities[i * 3 + 1] = this.velocities[i * 3 + 1]! * drag - this.gravity[i]! * dt;
      this.velocities[i * 3 + 2] = this.velocities[i * 3 + 2]! * drag;
      this.positions[i * 3] = this.positions[i * 3]! + this.velocities[i * 3]! * dt;
      this.positions[i * 3 + 1] = Math.max(
        0.05,
        this.positions[i * 3 + 1]! + this.velocities[i * 3 + 1]! * dt,
      );
      this.positions[i * 3 + 2] = this.positions[i * 3 + 2]! + this.velocities[i * 3 + 2]! * dt;
      this.alphas[i] = Math.min(1, t * 2.5);
      this.sizes[i] = this.baseSize[i]! * (0.6 + 0.4 * Math.min(1, (1 - t) * 6));
      this.spins[i] = this.spins[i]! + dt * 3;
    }
    const geometry = this.points.geometry;
    for (const name of ['position', 'color', 'aSize', 'aAlpha', 'aShape', 'aSpin']) {
      geometry.getAttribute(name).needsUpdate = true;
    }
  }
}
